import React, { useState } from 'react'
import { COLORS } from '@btime/constants'
import { useTranslation } from 'react-i18next'

import { useChecklistFormActions, useSelectedQuestion } from '../../../../../_form/ChecklistFormHandler'
import Text, { SmallText, Title } from '../../../../../../../../packages/kiti/components/presentation/Text'
import FieldValueSelect from '../../../../../../../../containers/fieldValues/select/FieldValueSelect'
import LineSeparator from '../../../../../../../../packages/kiti/components/helpers/LineSeparator'
import { usePlaceTypesWithPermission } from '../../../../../../../../utils/helpers/placeTypes'
import Separator from '../../../../../../../../packages/kiti/components/helpers/Separator'
import ToRight from '../../../../../../../../packages/kiti/components/helpers/ToRight'
import Select from '../../../../../../../../packages/kiti/components/form/Select'
import Link from '../../../../../../../../packages/kiti/components/actions/Link'
import { HorizontalLine } from '../../../../../../../../components/shapes/Line'
import DynamicInput from '../../../../../../../../components/form/DynamicInput'
import { canRenderInputForType } from '../../_helpers/hideInputForType'
import FormItem from '../../../../../../../../components/form/FormItem'
import Modal from '../../../../../../../../components/structure/Modal'
import LabelValue from '../../../../../../../../packages/kiti/components/presentation/LabelValue'
import Button from '../../../../../../../../packages/kiti/components/actions/Button'
import Panel from '../../../../../../../../packages/kiti/components/structure/Panel'
import Icon from '../../../../../../../../packages/kiti/components/shapes/Icon'
import { ALLOWED_FIELD_TYPES, INTERPOLATIONS, getOperators } from './_helpers/constants'
import { formatDynamicValue } from './_helpers/formatDynamicValue'

const FilterByFieldValueModal = ({ onClose }) => {
  const { t } = useTranslation('editChecklist')
  const selectedQuestion = useSelectedQuestion()
  const placeTypes = usePlaceTypesWithPermission('list')
  const { handleSelectedQuestionChangeOnPath } = useChecklistFormActions()

  const [fieldValue, setFieldValue] = useState(selectedQuestion.data?.filterByFieldValue?.fieldValue || '')
  const [operator, setOperator] = useState(selectedQuestion.data?.filterByFieldValue?.operator || '')
  const [value, setValue] = useState(selectedQuestion.data?.filterByFieldValue?.value || '')

  const origins = placeTypes.map(type => type.key)

  const handleFieldValue = value => {
    if (!value) {
      setFieldValue('')
      setOperator('')
      setValue('')
      return
    }
    setValue('')
    setOperator('')
    setFieldValue(value)
  }

  const handleOperator = operator => {
    if (!operator) {
      setValue('')
      setOperator('')
      return
    }
    setOperator(operator)
  }

  const handleValue = value => {
    if (!value) {
      setValue('')
    }
    setValue(value)
  }

  const handleSubmit = () => {
    handleSelectedQuestionChangeOnPath(['data', 'filterByFieldValue'], { value, operator, fieldValue })
    onClose?.()
  }

  return (
    <Modal open={true} onClose={onClose} width="28rem">
      <Title size="h6">{t('filterByAdditionalField')}</Title>
      <LineSeparator />
      <Separator weight={6} />
      <FormItem label={t('selectAdditionalField')} weight={2}>
        <FieldValueSelect origin={origins} onChange={handleFieldValue} value={fieldValue} type={ALLOWED_FIELD_TYPES} />
      </FormItem>
      <FormItem label={t('selectOperator')} weight={2}>
        <Select options={getOperators(t)} onChange={handleOperator} value={operator} disabled={!fieldValue} />
      </FormItem>
      <FormItem label={t('value')} weight={2}>
        <DynamicInput
          value={value}
          onChange={handleValue}
          valueType={!operator ? undefined : fieldValue?.type}
          interpolations={INTERPOLATIONS}
          disabled={!operator}
        />
      </FormItem>
      <Separator weight={6} />
      <ToRight>
        <Button disabled={!fieldValue || !operator || !value} onClick={handleSubmit}>
          {t('addFilter')}
        </Button>
      </ToRight>
    </Modal>
  )
}

const FilterByFieldValueSection = () => {
  const { t } = useTranslation('editChecklist')
  const selectedQuestion = useSelectedQuestion()
  const shouldRender = canRenderInputForType(selectedQuestion.questionType.name, 'place')
  const { handleSelectedQuestionChangeOnPath } = useChecklistFormActions()
  const [open, setOpen] = useState(false)

  const hasFilter = selectedQuestion.data?.filterByFieldValue

  const fieldValue = selectedQuestion.data?.filterByFieldValue?.fieldValue?.name
  const operator = selectedQuestion.data?.filterByFieldValue?.operator?.name
  const value = selectedQuestion.data?.filterByFieldValue?.value
  const fieldType = selectedQuestion.data?.filterByFieldValue?.fieldValue?.type

  const formattedValue = formatDynamicValue(fieldType, value)

  const handleRemoveFilter = () => {
    delete selectedQuestion.data?.filterByFieldValue
    handleSelectedQuestionChangeOnPath(['data'], selectedQuestion.data)
  }

  if (!shouldRender) return false

  return (
    <>
      <Separator weight={6} />
      <Title size="h6" color={COLORS.dark4}>
        {t('filterByAdditionalField')}
      </Title>
      <HorizontalLine thin lineColor={COLORS.light2} />
      <Separator weight={4} />
      <Panel
        borderStyle={hasFilter ? 'solid' : 'dashed'}
        align={hasFilter ? 'start' : 'center'}
        justify="center"
        height={8.5}
        padding={0.5}
        onClick={() => setOpen(true)}
      >
        {!hasFilter ? (
          <>
            <Icon name="BsPlusSquareDotted" color="dark3" />
            <Separator weight={2} />
            <Text bold color="dark3">
              {t('addFilter')}
            </Text>
          </>
        ) : (
          <>
            <LabelValue label={t('additionalField')} value={fieldValue} color="dark3" size="small" />
            <LabelValue label={t('selectOperator')} value={operator} color="dark3" size="small" />
            <LabelValue label={t('value')} value={formattedValue} color="dark3" size="small" />
          </>
        )}
      </Panel>
      <Separator weight={2} />
      {hasFilter && (
        <Link onClick={handleRemoveFilter}>
          <SmallText bold color={COLORS.red}>
            {t('removeFilter')}
          </SmallText>
        </Link>
      )}
      <Separator weight={4} />
      {open && <FilterByFieldValueModal open={open} onClose={() => setOpen(false)} />}
    </>
  )
}

export default FilterByFieldValueSection
